
import React from 'react';
import { motion } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { fadeIn, slideIn, getReducedMotionVariants } from '../utils/motion';

interface ComparisonPageProps {
  reducedMotion: boolean;
}

const ComparisonPage: React.FC<ComparisonPageProps> = ({ reducedMotion }) => {
  const traditionalApproach = [
    "1-2 basic online calculators",
    "Generic qualification estimates",
    "No property comparison tools",
    "Hidden costs revealed late",
    "Limited investment analysis",
    "No AI assistance",
    "Stressful last-minute surprises"
  ];

  const equiforgeApproach = [
    "10+ integrated specialized tools",
    "Loan-specific qualification with 99.9% accuracy",
    "Side-by-side property comparison",
    "Complete cost transparency from day one",
    "Comprehensive ROI and investment analytics",
    "C.L.A.R.A. AI assistant available 24/7",
    "Peace of mind throughout the entire journey"
  ];

  return (
    <div className="space-y-10">
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-2"
      >
        <h2 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
          The Difference is Clear
        </h2>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          See how EquiForge's comprehensive platform transforms the traditional homebuying experience
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Traditional Approach */}
        <motion.div
          variants={getReducedMotionVariants(slideIn('left', 0.2, 0.6), reducedMotion)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6"
        >
          <h3 className="text-2xl font-bold text-red-500 dark:text-red-400 flex items-center">
            <span className="mr-2 bg-red-100 dark:bg-red-900/30 p-1 rounded-full">
              <X className="h-4 w-4" />
            </span>
            The Guesswork Approach
          </h3>
          
          <ul className="space-y-4">
            {traditionalApproach.map((item, index) => (
              <motion.li 
                key={index}
                variants={getReducedMotionVariants(fadeIn('up', 0.3 + index * 0.1), reducedMotion)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-start space-x-3"
              >
                <span className="bg-red-100 dark:bg-red-900/30 p-1 rounded-full mt-0.5">
                  <X className="h-4 w-4 text-red-500 dark:text-red-400" />
                </span>
                <span className="text-muted-foreground">{item}</span>
              </motion.li>
            ))}
          </ul>
          
          <div className="p-4 border border-red-200 dark:border-red-900/30 rounded-lg bg-red-50 dark:bg-red-900/10">
            <p className="text-sm text-red-700 dark:text-red-300">
              Traditional approaches leave homebuyers vulnerable to costly mistakes and stressful surprises.
            </p>
          </div>
        </motion.div>

        {/* EquiForge Approach */}
        <motion.div
          variants={getReducedMotionVariants(slideIn('right', 0.2, 0.6), reducedMotion)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6"
        >
          <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 flex items-center">
            <span className="mr-2 bg-green-100 dark:bg-green-900/30 p-1 rounded-full">
              <Check className="h-4 w-4" />
            </span>
            The EquiForge Platform
          </h3>
          
          <ul className="space-y-4">
            {equiforgeApproach.map((item, index) => (
              <motion.li 
                key={index}
                variants={getReducedMotionVariants(fadeIn('up', 0.3 + index * 0.1), reducedMotion)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="flex items-start space-x-3"
              >
                <span className="bg-green-100 dark:bg-green-900/30 p-1 rounded-full mt-0.5">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                </span>
                <span className="font-medium">{item}</span>
              </motion.li>
            ))}
          </ul>
          
          <div className="p-4 border border-green-200 dark:border-green-900/30 rounded-lg bg-green-50 dark:bg-green-900/10">
            <p className="text-sm text-green-700 dark:text-green-300">
              With EquiForge, homebuyers gain clarity, confidence, and control throughout their entire journey.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ComparisonPage;
